import React, {useEffect, useMemo, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import {toastError, toastSuccess} from "../../../helpers/Toast";

const emptyAnswer = () => ({id: null, text: "", correct: false});
const emptyQuestion = () => ({id: null, text: "", multipleAnswers: false, answers: [emptyAnswer(), emptyAnswer()]});
const emptyTest = () => ({
  id: null,
  trainingId: "",
  departmentId: "",
  title: "",
  instructions: "",
  passingScorePercent: 70,
  maxAttempts: 3,
  active: true,
  questions: [emptyQuestion()]
});

const errorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const TrainingTestsEditor = () => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const roles = Array.isArray(auth?.roles) ? auth.roles : String(auth?.roles || "").replace(/[\[\]\s]/g, "").split(",");
  const isManagerOnly = roles.includes("ROLE_MANAGER") && !roles.includes("ROLE_HR") && !roles.includes("ROLE_ADMIN");
  const [tests, setTests] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyTest());

  const trainingOptions = useMemo(() => {
    const usedTrainingIds = new Set(tests.map(test => Number(test.trainingId)));
    return [...trainings]
      .filter(training => training.formType === "QUIZ" || usedTrainingIds.has(Number(training.id)))
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pl"));
  }, [trainings, tests]);

  const departmentOptions = useMemo(() => [...departments]
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pl")), [departments]);

  const loadData = async () => {
    setLoading(true);
    try {
      const departmentUrl = isManagerOnly ? "/seasonal-availability/departments" : "/departments";
      const [testsResponse, trainingsResponse, departmentsResponse] = await Promise.all([
        axiosPrivate.get("/training-tests"),
        axiosPrivate.get("/trainings"),
        axiosPrivate.get(departmentUrl)
      ]);
      setTests(testsResponse.data || []);
      setTrainings(trainingsResponse.data || []);
      setDepartments(departmentsResponse.data || []);
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [axiosPrivate, isManagerOnly]);

  const openNew = () => {
    const next = emptyTest();
    if (isManagerOnly && departmentOptions.length === 1) next.departmentId = String(departmentOptions[0].id);
    setForm(next);
    setShowModal(true);
  };

  const openEdit = test => {
    setForm({
      id: test.id,
      trainingId: String(test.trainingId || ""),
      departmentId: test.departmentId ? String(test.departmentId) : "",
      title: test.title || "",
      instructions: test.instructions || "",
      passingScorePercent: test.passingScorePercent ?? 70,
      maxAttempts: test.maxAttempts ?? 3,
      active: Boolean(test.active),
      questions: (test.questions || []).map(question => ({
        id: question.id,
        text: question.text || "",
        multipleAnswers: Boolean(question.multipleAnswers),
        answers: (question.answers || []).map(answer => ({id: answer.id, text: answer.text || "", correct: Boolean(answer.correct)}))
      }))
    });
    setShowModal(true);
  };

  const updateQuestion = (questionIndex, patch) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((question, index) => index === questionIndex ? {...question, ...patch} : question)
    }));
  };

  const updateAnswer = (questionIndex, answerIndex, patch) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((question, qIndex) => {
        if (qIndex !== questionIndex) return question;
        const nextAnswers = question.answers.map((answer, aIndex) => aIndex === answerIndex ? {...answer, ...patch} : answer);
        if (patch.correct === true && !question.multipleAnswers) {
          return {...question, answers: nextAnswers.map((answer, aIndex) => ({...answer, correct: aIndex === answerIndex}))};
        }
        return {...question, answers: nextAnswers};
      })
    }));
  };

  const addQuestion = () => setForm(prev => ({...prev, questions: [...prev.questions, emptyQuestion()]}));
  const removeQuestion = questionIndex => setForm(prev => ({...prev, questions: prev.questions.filter((_, index) => index !== questionIndex)}));

  const addAnswer = questionIndex => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((question, index) => index === questionIndex ? {...question, answers: [...question.answers, emptyAnswer()]} : question)
    }));
  };

  const removeAnswer = (questionIndex, answerIndex) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((question, index) => index === questionIndex ? {...question, answers: question.answers.filter((_, aIndex) => aIndex !== answerIndex)} : question)
    }));
  };

  const toggleMultiple = questionIndex => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((question, index) => {
        if (index !== questionIndex) return question;
        if (question.multipleAnswers) {
          const firstCorrect = question.answers.findIndex(answer => answer.correct);
          return {
            ...question,
            multipleAnswers: false,
            answers: question.answers.map((answer, answerIndex) => ({...answer, correct: firstCorrect >= 0 && answerIndex === firstCorrect}))
          };
        }
        return {...question, multipleAnswers: true};
      })
    }));
  };

  const validate = () => {
    if (!form.trainingId || !form.title.trim()) return "Wybierz typ szkolenia i podaj nazwę testu.";
    if (isManagerOnly && !form.departmentId) return "Kierownik musi wskazać dział, dla którego tworzony jest test.";
    const passing = Number(form.passingScorePercent);
    if (!Number.isFinite(passing) || passing < 0 || passing > 100) return "Próg zaliczenia musi mieścić się w zakresie 0-100%.";
    const maxAttempts = Number(form.maxAttempts);
    if (!Number.isInteger(maxAttempts) || maxAttempts < 1 || maxAttempts > 100) return "Maksymalna liczba podejść musi mieścić się w zakresie 1-100.";
    if (!form.questions.length) return "Dodaj co najmniej jedno pytanie.";
    for (let i = 0; i < form.questions.length; i++) {
      const question = form.questions[i];
      if (!question.text.trim()) return `Uzupełnij treść pytania ${i + 1}.`;
      if (question.answers.length < 2) return `Pytanie ${i + 1} musi mieć co najmniej dwie odpowiedzi.`;
      if (question.answers.some(answer => !answer.text.trim())) return `Uzupełnij wszystkie odpowiedzi w pytaniu ${i + 1}.`;
      const correctCount = question.answers.filter(answer => answer.correct).length;
      if (correctCount === 0) return `Wskaż poprawną odpowiedź w pytaniu ${i + 1}.`;
      if (!question.multipleAnswers && correctCount !== 1) return `Pytanie ${i + 1} może mieć tylko jedną poprawną odpowiedź.`;
    }
    return null;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      toastError(validationError);
      return;
    }
    const payload = {
      id: form.id,
      trainingId: Number(form.trainingId),
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      title: form.title.trim(),
      instructions: form.instructions.trim() || null,
      passingScorePercent: Number(form.passingScorePercent),
      maxAttempts: Number(form.maxAttempts),
      active: Boolean(form.active),
      questions: form.questions.map((question, questionIndex) => ({
        id: question.id,
        text: question.text.trim(),
        multipleAnswers: Boolean(question.multipleAnswers),
        sortOrder: questionIndex + 1,
        answers: question.answers.map((answer, answerIndex) => ({
          id: answer.id,
          text: answer.text.trim(),
          correct: Boolean(answer.correct),
          sortOrder: answerIndex + 1
        }))
      }))
    };

    setSaving(true);
    try {
      if (form.id) {
        await axiosPrivate.put(`/training-tests/${form.id}`, payload);
        toastSuccess("Zaktualizowano test szkoleniowy.");
      } else {
        await axiosPrivate.post("/training-tests", payload);
        toastSuccess("Dodano test szkoleniowy.");
      }
      setShowModal(false);
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async test => {
    if (!window.confirm(`Usunąć test „${test.title}”?`)) return;
    try {
      await axiosPrivate.delete(`/training-tests/${test.id}`);
      toastSuccess("Usunięto test szkoleniowy.");
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  return (
    <>
      <div className="card shadow p-3 mb-4 bg-white rounded-2">
        <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
          <div>
            <h5 className="mb-1">Definicje testów</h5>
            <div className="text-muted small">Test można utworzyć tylko dla typu szkolenia oznaczonego jako QUIZ. Test może być globalny albo przypisany do konkretnego działu.</div>
          </div>
          <Button onClick={openNew}>Dodaj test</Button>
        </div>

        {loading ? <div>Ładowanie danych...</div> : tests.length === 0 ? <div className="text-muted">Nie zdefiniowano jeszcze żadnych testów.</div> : (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead><tr><th>Szkolenie</th><th>Dział</th><th>Test</th><th>Wersja</th><th>Pytań</th><th>Próg</th><th>Podejścia</th><th>Status</th><th></th></tr></thead>
              <tbody>
              {tests.map(test => (
                <tr key={test.id}>
                  <td>{test.trainingName}</td>
                  <td>{test.departmentName || "Globalny"}</td>
                  <td><strong>{test.title}</strong></td>
                  <td>{test.definitionVersion || 1}</td>
                  <td>{test.questions?.length || 0}</td>
                  <td>{test.passingScorePercent}%</td>
                  <td>{test.maxAttempts}</td>
                  <td><span className={test.active ? "text-success fw-bold" : "text-secondary"}>{test.active ? "AKTYWNY" : "WYŁĄCZONY"}</span></td>
                  <td className="text-nowrap text-end">
                    <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(test)}>Edytuj</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(test)}>Usuń</Button>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => !saving && setShowModal(false)} size="xl" backdrop="static">
        <Modal.Header closeButton={!saving}><Modal.Title>{form.id ? "Edycja testu szkoleniowego" : "Nowy test szkoleniowy"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <Form.Label>Typ szkolenia</Form.Label>
              <Form.Select value={form.trainingId} onChange={event => setForm(prev => ({...prev, trainingId: event.target.value}))}>
                <option value="">-- wybierz QUIZ --</option>
                {trainingOptions.map(training => <option key={training.id} value={training.id}>{training.name}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label>Dział</Form.Label>
              <Form.Select value={form.departmentId} onChange={event => setForm(prev => ({...prev, departmentId: event.target.value}))}>
                {!isManagerOnly && <option value="">Globalny / wszystkie działy</option>}
                {departmentOptions.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-5">
              <Form.Label>Nazwa testu</Form.Label>
              <Form.Control value={form.title} onChange={event => setForm(prev => ({...prev, title: event.target.value}))}/>
            </div>
            <div className="col-md-3">
              <Form.Label>Próg zaliczenia %</Form.Label>
              <Form.Control type="number" min="0" max="100" step="0.01" value={form.passingScorePercent} onChange={event => setForm(prev => ({...prev, passingScorePercent: event.target.value}))}/>
            </div>
            <div className="col-md-3">
              <Form.Label>Maksymalna liczba podejść</Form.Label>
              <Form.Control type="number" min="1" max="100" step="1" value={form.maxAttempts} onChange={event => setForm(prev => ({...prev, maxAttempts: event.target.value}))}/>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Instrukcja dla pracownika</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.instructions} onChange={event => setForm(prev => ({...prev, instructions: event.target.value}))}/>
          </Form.Group>
          <Form.Check className="mb-4" type="switch" label="Test aktywny" checked={form.active} onChange={event => setForm(prev => ({...prev, active: event.target.checked}))}/>

          <div className="d-flex justify-content-between align-items-center mb-2"><h6 className="mb-0">Pytania</h6><Button size="sm" variant="outline-primary" onClick={addQuestion}>Dodaj pytanie</Button></div>
          <div className="d-grid gap-3">
            {form.questions.map((question, questionIndex) => (
              <div className="border rounded p-3" key={`question-${questionIndex}`}>
                <div className="d-flex justify-content-between gap-3 mb-2">
                  <div className="flex-grow-1">
                    <Form.Label>Pytanie {questionIndex + 1}</Form.Label>
                    <Form.Control as="textarea" rows={2} value={question.text} onChange={event => updateQuestion(questionIndex, {text: event.target.value})}/>
                  </div>
                  <div className="pt-4"><Button size="sm" variant="outline-danger" onClick={() => removeQuestion(questionIndex)} disabled={form.questions.length === 1}>Usuń pytanie</Button></div>
                </div>
                <Form.Check className="mb-3" type="switch" label="Wiele poprawnych odpowiedzi" checked={question.multipleAnswers} onChange={() => toggleMultiple(questionIndex)}/>
                <div className="d-grid gap-2">
                  {question.answers.map((answer, answerIndex) => (
                    <div className="d-flex align-items-center gap-2" key={`answer-${questionIndex}-${answerIndex}`}>
                      <Form.Check type={question.multipleAnswers ? "checkbox" : "radio"} name={`correct-${questionIndex}`} checked={answer.correct} onChange={event => updateAnswer(questionIndex, answerIndex, {correct: event.target.checked})}/>
                      <Form.Control value={answer.text} placeholder={`Odpowiedź ${answerIndex + 1}`} onChange={event => updateAnswer(questionIndex, answerIndex, {text: event.target.value})}/>
                      <Button size="sm" variant="outline-danger" onClick={() => removeAnswer(questionIndex, answerIndex)} disabled={question.answers.length <= 2}>Usuń</Button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => addAnswer(questionIndex)}>Dodaj odpowiedź</Button>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Anuluj</Button><Button onClick={save} disabled={saving}>{saving ? "Zapisywanie..." : "Zapisz test"}</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export default TrainingTestsEditor;
